import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type SetURLSearchParams } from 'react-router';

import { readVisibility, writeVisibility } from '@/entities/board';

import { type SharedSources, sameIds } from './sources';

import {
  type CalendarDefaults,
  type CalendarState,
  readCalendarState,
  rememberView,
  visibilityOf,
  writeCalendarState,
} from './model';

/**
 * The calendar's view, date and filters live in the query string, so a reload, a shared link
 * and back / forward keep them. Updates go through a ref because react-router's setter reads
 * the params of the last render, which would make quick successive updates overwrite each other.
 *
 * The four primary switches (tasks, events, completed, overdue) are also remembered PER SCOPE
 * (`storageKey`: one board, or one workspace) in localStorage. The URL wins when it carries a
 * value; otherwise the remembered one applies, so a reload or reopening the calendar keeps them
 * and another board's calendar never inherits them.
 *
 * The GLOBAL calendar passes `sources`: its hidden boards are then the selection shared with the
 * Home calendar, not a per-scope value. A `hide` parameter in the URL wins for that navigation and
 * is adopted into the shared selection when it changes (a bare URL means "the shared selection");
 * a change made here is stored and written to the URL.
 *
 * The view (mode, density) the URL does not name comes from `defaults`; choosing one is
 * remembered per device class (`device`), so a phone and a desktop keep their own.
 */
export function useCalendarState(
  params: URLSearchParams,
  setParams: SetURLSearchParams,
  today: string,
  storageKey: string,
  {
    defaults,
    device,
    sources,
  }: {
    defaults: CalendarDefaults;
    device: 'desktop' | 'phone';
    sources?: SharedSources & { known: ReadonlySet<string> };
  },
): [CalendarState, (patch: Partial<CalendarState>) => void] {
  // The remembered switches of THIS scope. A switch changed to exactly what is remembered leaves
  // the URL untouched, so the remembered value lives in state: changing it re-reads the state.
  const [remembered, setRemembered] = useState(() => ({
    key: storageKey,
    prefs: readVisibility(storageKey),
  }));
  if (remembered.key !== storageKey) {
    setRemembered({ key: storageKey, prefs: readVisibility(storageKey) }); // another board
  }
  const stored = remembered.key === storageKey ? remembered.prefs : readVisibility(storageKey);
  const urlHide = params.get('hide');
  const state = useMemo(() => {
    const read = readCalendarState(params, today, stored, defaults);
    if (!sources) return read;
    const hidden = urlHide === null ? sources.hidden : read.hiddenBoardIds;
    return { ...read, hiddenBoardIds: hidden.filter((id) => sources.known.has(id)) };
  }, [params, today, stored, defaults, sources, urlHide]);
  const sourcesRef = useRef(sources);
  useEffect(() => {
    sourcesRef.current = sources;
  });
  // A URL that names the hidden boards becomes the shared selection - once per URL change, so a
  // change made here (store first, URL right after) never ping-pongs with this.
  useEffect(() => {
    const shared = sourcesRef.current;
    if (!shared || urlHide === null) return;
    const ids = urlHide.split(',').filter(Boolean);
    if (!sameIds(ids, shared.hidden)) shared.remember(ids);
  }, [urlHide]);
  const pending = useRef<CalendarState | null>(null);
  useEffect(() => {
    pending.current = null;
  }, [params]);
  const update = useCallback(
    (patch: Partial<CalendarState>) => {
      const next = { ...(pending.current ?? state), ...patch };
      pending.current = next;
      if (sources && patch.hiddenBoardIds) sources.remember(patch.hiddenBoardIds);
      if ('mode' in patch || 'density' in patch) {
        rememberView(storageKey, device, { mode: next.mode, density: next.density });
      }
      // Remember the switches first: the URL is then written relative to what is remembered.
      const prefs = visibilityOf(next);
      writeVisibility(storageKey, prefs);
      setRemembered({ key: storageKey, prefs });
      setParams(writeCalendarState(params, next, prefs, defaults), { replace: true });
    },
    [params, setParams, state, storageKey, device, defaults, sources],
  );
  return [state, update];
}
